import React, { useState, useEffect } from 'react';
import { formatPrice } from '../data/keszHazakModels';

const MortgageCard = ({ model, className = '' }) => {
  const [loanAmount, setLoanAmount] = useState(model.price_from_eur);
  const [termYears, setTermYears] = useState(model.mortgage?.example_term_years || 30);
  const [interestRate, setInterestRate] = useState(model.mortgage?.example_rate_percent || 3.86);
  const [monthlyPayment, setMonthlyPayment] = useState(0);

  // Havi törlesztő számítása (annuitás képlet)
  const calculateMonthlyPayment = () => {
    const principal = loanAmount;
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = termYears * 12;

    if (monthlyRate === 0) {
      return principal / numberOfPayments;
    }

    const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                          (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    
    return monthlyPayment;
  };

  useEffect(() => {
    setMonthlyPayment(calculateMonthlyPayment());
  }, [loanAmount, termYears, interestRate]);

  return (
    <div className={`bg-[#161616] backdrop-blur-xl rounded-xl border border-white/[0.08] p-6 ${className}`}>
      <div className="mb-6">
        <h3 className="text-lg font-bold text-white mb-2 flex items-center">
          <svg className="w-5 h-5 mr-2 text-[#e31937]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
          Példa havi részlet
        </h3>
        <p className="text-sm text-gray-300">
          Irányadó törlesztő kalkuláció
        </p>
      </div>

      <div className="space-y-4">
        {/* Hitelösszeg */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Hitelösszeg
          </label>
          <div className="relative">
            <input
              type="range"
              min={model.price_from_eur * 0.5}
              max={model.price_from_eur * 1.5}
              step={1000}
              value={loanAmount}
              onChange={(e) => setLoanAmount(parseInt(e.target.value))}
              className="w-full h-3 bg-white/20 rounded-lg appearance-none cursor-pointer slider-hopphomes"
              style={{
                background: `linear-gradient(to right, #e31937 0%, #e31937 ${((loanAmount - model.price_from_eur * 0.5) / (model.price_from_eur * 1.5 - model.price_from_eur * 0.5)) * 100}%, rgba(255,255,255,0.2) ${((loanAmount - model.price_from_eur * 0.5) / (model.price_from_eur * 1.5 - model.price_from_eur * 0.5)) * 100}%, rgba(255,255,255,0.2) 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-300 mt-2">
              <span>{formatPrice(model.price_from_eur * 0.5)}</span>
              <span className="font-semibold text-[#e31937] bg-white/10 px-2 py-1 rounded">{formatPrice(loanAmount)}</span>
              <span>{formatPrice(model.price_from_eur * 1.5)}</span>
            </div>
          </div>
        </div>

        {/* Futamidő */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Futamidő: <span className="text-[#e31937]">{termYears} év</span>
          </label>
          <input
            type="range"
            min="10"
            max="35"
            step="1"
            value={termYears}
            onChange={(e) => setTermYears(parseInt(e.target.value))}
            className="w-full h-3 bg-white/20 rounded-lg appearance-none cursor-pointer slider-hopphomes"
            style={{
              background: `linear-gradient(to right, #e31937 0%, #e31937 ${((termYears - 10) / (35 - 10)) * 100}%, rgba(255,255,255,0.2) ${((termYears - 10) / (35 - 10)) * 100}%, rgba(255,255,255,0.2) 100%)`
            }}
          />
          <div className="flex justify-between text-xs text-gray-300 mt-2">
            <span>10 év</span>
            <span>35 év</span>
          </div>
        </div>

        {/* Kamat */}
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Éves kamat: <span className="text-[#e31937]">{interestRate}%</span>
          </label>
          <input
            type="range"
            min="2"
            max="8"
            step="0.1"
            value={interestRate}
            onChange={(e) => setInterestRate(parseFloat(e.target.value))}
            className="w-full h-3 bg-white/20 rounded-lg appearance-none cursor-pointer slider-hopphomes"
            style={{
              background: `linear-gradient(to right, #e31937 0%, #e31937 ${((interestRate - 2) / (8 - 2)) * 100}%, rgba(255,255,255,0.2) ${((interestRate - 2) / (8 - 2)) * 100}%, rgba(255,255,255,0.2) 100%)`
            }}
          />
          <div className="flex justify-between text-xs text-gray-300 mt-2">
            <span>2%</span>
            <span>8%</span>
          </div>
        </div>
      </div>

      {/* Eredmény */}
      <div className="mt-6 p-4 bg-gradient-to-r from-[#e31937] to-[#b40000] rounded-lg border border-[#e31937]/50 shadow-lg">
        <div className="text-center">
          <div className="text-2xl font-bold text-white mb-1">
            {formatPrice(monthlyPayment)}/hó
          </div>
          <div className="text-sm text-white/80">
            Irányadó havi törlesztő
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="mt-4 p-3 bg-white/5 border border-white/[0.08] rounded-lg">
        <div className="flex items-start space-x-2">
          <svg className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div className="text-xs text-gray-300">
            <strong>Figyelem:</strong> A kalkuláció tájékoztató jellegű; a tényleges feltételek pénzügyi partner bírálata alapján alakulnak. Az itt jelzett törlesztő csak tájékoztató. A tényleges kondíciók banki bírálat függvénye.
          </div>
        </div>
      </div>
    </div>
  );
};

export default MortgageCard;
